import { CachePolicies, useFetch } from 'use-http';
import { Signal } from '@preact/signals-react';
import produce from 'immer';
import {
  EditorDeclarativeBlock,
  PlacementKind,
} from '@/webapi/use-experience-api';
import { routes } from '@/webapi/routes';
import { QBItemSelection } from '@/components/query-builder/models';
import { DeviceType } from '@/utils/definitions';

import { SlotState } from '@/features/editor/widgets/custom-widget/loading-section/shared/models';
import { RuleState } from '@/features/editor/widgets/custom-widget/loading-section/layered-ruling/context';
import { CompoundChange, StickinessMode } from '@/pkg/sdk';
import { StoreStyles } from '@/features/editor/widgets/custom-widget/style-templating/models';

export function useWidgetCatalogApi(_loading: boolean) {
  const { get, post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: _loading,
    },
  }));

  const getAllApps = async () => {
    const res = (await get(routes.listAllApps())) as {
      apps: CatalogApp[];
      storeStyles?: StoreStyles;
    };
    if (res?.apps) {
      res.apps = res?.apps?.map((app) => {
        app.widgets = app?.widgets?.map((w) => {
          w.blueprint.schema.appId = app.id;
          return w;
        });
        app.widgets = app.widgets.sort((a, b) => a.order - b.order);
        return app;
      });
    }
    return res;
  };

  const getChange = async (experienceId: string, loomiId: string) =>
    (await post(routes.getChange(), {
      experienceId,
      loomiId,
    })) as {
      change: EditorDeclarativeBlock;
    };

  const saveAiWidget = async (data: AiWidgetData) =>
    (await post(routes.saveAiWidget(), data)) as any;

  const deleteSavedWidgets = async (id?: string) =>
    (await post(routes.deleteSavedWidgets(), {
      id,
    })) as GetSavedWidgetsOutput;

  const getSavedWidgets = async (search?: string, page?: number) =>
    (await post(routes.getSavedWidgets(), {
      search,
      page,
    })) as GetSavedWidgetsOutput;

  const applySavedWidgetToChange = (
    widget: AiWidgetData,
    change: Signal<EditorDeclarativeBlock>,
  ) => {
    change.value = produce(change.value, (draft) => {
      draft.hint = widget?.extra?.hint;
      const block = draft.block.value as CompoundChange;
      block.html = widget?.html?.replaceAll?.(`{section_id}`, draft.editorId);
      block.css = widget?.css?.replaceAll?.(`{section_id}`, draft.editorId);
      block.js = widget?.js?.replaceAll?.(`{section_id}`, draft.editorId);
      block.aiSessionId = ``;
      block.isAiComponent = true;
      block.editorConfig = widget?.extra?.editorConfig;
      block.editManifest = widget?.extra?.editManifest;
      block.recsStrategies = widget?.extra?.recsStrategies;
      block.isFromBookmark = widget?.id;
    });
  };

  return {
    getAllApps,
    getChange,
    saveAiWidget,
    getSavedWidgets,
    applySavedWidgetToChange,
    deleteSavedWidgets,
    loading,
  };
}

export interface CatalogApp {
  id: string;
  name: string;
  hint: string;
  image: string;
  widgets?: CatalogWidget[];
  relatedPlacements?: PlacementKind[];
  isHidden?: boolean;
}

export interface CatalogWidget {
  id: string;
  version?: string;
  name: string;
  image: string;
  hint: string;
  blueprint: WidgetBlueprint;
  relatedPlacements?: PlacementKind[];
  isHidden?: boolean;
  group?: string;
  order?: number;
}

export interface CatalogWidgetProps {
  customizations: Customization[];
  settings: Settings;
  groups?: Groups;
  appId?: string;
  styleTemplateId?: string;
  tsSelected?: number;
  blueprintName?: string;
  suggestAltTet?: boolean;
}

export interface Groups {
  primary: string;
  mapping: Record<string, string[]>;
}

export function getRecStrategy(currentParams: CatalogWidgetProps) {
  return currentParams?.settings?.loading?.value?.value;
}

export interface WidgetBlueprint {
  script: string;
  schema: CatalogWidgetProps;
}

export interface Customization {
  key: string;
  name: string;
  canBeDisabled?: boolean;
  isDisabled?: boolean;
  hideOn?: DeviceType[];
  hideWhen?: string;
  components: Component[];
  hasHoverState?: boolean;
  draggable?: boolean;
  defaultComponents?: Component[];
}

export interface Component {
  key: string;
  name: string;
  hideOn?: DeviceType[];
  hideWhen?: string;
  specs: CustomizationSpec[];
}

export interface Settings {
  quickEdit: QuickEdit[];
  general?: GeneralSetting;
  loading?: RecStrategy;
  uiByGroup?: string;
  quickEditGroups?: QuickEditGroup[];
}

export interface QuickEditGroup {
  name: string;
  quickEdit: QuickEdit[];
}

export interface RecStrategy {
  title: string;
  value: LoadingStrategyOption;
  loadingEnv?: LoadingEnv;
  options: LoadingStrategyOption[];
}

export enum RecommendationType {
  MANUAL = `MANUAL`,
  CART_ITEMS = `CART_ITEMS`,
  PERSONALIZED = `PERSONALIZED`,
  MOST_POPULAR = `MOST_POPULAR`,
  NEW_ARRIVALS = `NEW_ARRIVALS`,
  VIEWD_TOGETHER = `VIEWD_TOGETHER`,
  RECENTLY_VIEWED = `RECENTLY_VIEWED`,
  PURCHASED_TOGETHER = `PURCHASED_TOGETHER`,
  ADVANCED_RULING = `ADVANCED_RULING`,
  PAST_PURCHASES = `PAST_PURCHASES`,
  MOST_CHEAP_IN_CART = `MOST_CHEAP_IN_CART`,
  MOST_EXPENSIVE_IN_CART = `MOST_EXPENSIVE_IN_CART`,
  VIEWED_WITH_RECENTLY_VIEWED = `VIEWED_WITH_RECENTLY_VIEWED`,
  PURCHASED_WITH_RECENTLY_PURCHASED = `PURCHASED_WITH_RECENTLY_PURCHASED`,
}

export interface LoadingEnv {
  recommendationOptions?: RecommendationOptions;
}

export interface ManualProduct {
  productId: string;
  mainValue: string;
  imageOverride: string;
}

export enum RequestOrigin {
  POST_PURCHASE = `post_purchase`,
  CHECKOUT_EXTENSIBILITY = `checkout_extensibility`,
}

export interface RecommendationOptions {
  requestOrigin?: RequestOrigin;
  type: RecommendationType;
  conditions?: any;
  strategyPerSlot?: Array<SlotState>;
  layeredRuling?: Array<RuleState>;
  productId?: string;
  productIds?: Array<string>;
  manualProducts?: Array<ManualProduct>;
  size?: number;
  storeAlias: string;
  env: number;
  conditionId?: string;
  condition?: Array<QBItemSelection>;
  // deprecated - use layeredRuling
  advancedRuling?: Array<AdvancedRulingOption>;
  appId?: string;
  widgetContext?: any;
}

export interface AdvancedRulingOption {
  num: number;
  manualProducts?: Array<ManualProduct>;
  itemSelection?: string;
  condition?: Array<QBItemSelection>;
}

export interface LoadingStrategyOption {
  label: string;
  description?: string;
  value: {
    default?: boolean;
    fn: string;
    template: string;
    type: RecommendationType;
  };
}

export interface QuickEdit {
  selector: string;
}

export interface GeneralSetting {
  title: string;
  key: string;
  specs: Spec[];
}

export interface CustomizationSpec {
  noAi?: boolean;
  explain?: string;
  key: string;
  name?: string;
  type: string;
  initialState?: boolean;
  hideOn?: DeviceType[];
  hideWhen?: string;
  values: Record<string, any>;
  __def_style?: string;
}

export interface Spec {
  key: string;
  type: string;
  hidden?: boolean;
  hideWhen?: string;
  value: Record<string, any>;
  metadata: Record<string, any>;
}

export interface PageRedirectOptions {
  sourceUrlProps?: Array<QBItemSelection>;
  targetUrl?: string;
  stickinessMode?: StickinessMode;
  retainQueryParams?: boolean;
}

export interface AiWidgetData {
  id: string; // Unique identifier for the widget
  name: string; // Widget name
  description: string; // Widget description
  image: string; // Image URL or base64 encoded image
  html: string; // HTML code for the widget
  css: string; // CSS styles for the widget
  js: string; // JavaScript code for the widget
  extra?: Record<string, any>; // Additional metadata (optional)
}

export interface GetSavedWidgetsOutput {
  widgets: AiWidgetData[]; // Array of saved widgets
  totalCount: number; // Total number of widgets matching the search
}
